import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  BuildingStorefrontIcon,
  MapPinIcon,
  CalendarIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

// Smart Farm Organisation ID — matches SetupFarm.js constant.
// Every user who registers a farm from the dashboard is linked to this org
// so admin queries (via user_org_farm) continue to see their farms.
const SMARTFARM_ORGANISATION_ID = 4345;

const MALAYSIAN_STATES = [
  'Selangor', 'Kuala Lumpur', 'Johor', 'Penang', 'Perak', 'Sabah',
  'Sarawak', 'Kedah', 'Kelantan', 'Terengganu', 'Pahang', 'Negeri Sembilan',
  'Melaka', 'Perlis', 'Putrajaya', 'Labuan',
];

/**
 * Create-Farm-With-First-Cycle wizard — a simplified 2-step flow that mirrors
 * the user registration journey (SetupFarm → SetupCycle).
 *
 * Step 1 – Farm: name, size, address (single clear block)
 * Step 2 – First Cycle: pick a produce, name the cycle, set dates + expected yield
 *
 * On success: farm + farm_user_role(owner) + user_org_farm + season are created
 * and the logged-in user is made the owner automatically. No Farmer ID,
 * Mobile Number, or Subscription Duration fields.
 */
const CreateFarmWithCycle = ({ onFarmCreated, onClose }) => {
  const { user } = useAuth();

  // Wizard
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Step 1: Farm fields
  const [farm, setFarm] = useState({
    farmName: '',
    farmSize: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postcode: '',
    latitude: '',
    longitude: '',
  });

  // Step 2: First cycle fields
  const today = new Date().toISOString().split('T')[0];
  const inNinetyDays = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 90);
    return d.toISOString().split('T')[0];
  })();
  const [cycle, setCycle] = useState({
    produce: null,               // selected produce object
    name: '',                    // cycle name
    dateStart: today,
    dateEnd: inNinetyDays,
    expectedHarvest: '',
  });
  const [produceQuery, setProduceQuery] = useState('');
  const [produceResults, setProduceResults] = useState([]);
  const [produceSearching, setProduceSearching] = useState(false);

  // ---------- Step 1 handlers ----------
  const setFarmField = (name, value) => {
    setFarm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: '' }));
  };

  const validateStep1 = () => {
    const next = {};
    if (!farm.farmName.trim()) next.farmName = 'Farm name is required';
    if (!farm.farmSize.trim()) next.farmSize = 'Farm size is required';
    else if (isNaN(parseFloat(farm.farmSize)) || parseFloat(farm.farmSize) <= 0)
      next.farmSize = 'Enter a positive number';
    if (!farm.addressLine1.trim()) next.addressLine1 = 'Address is required';
    if (!farm.city.trim()) next.city = 'City is required';
    if (!farm.state.trim()) next.state = 'State is required';
    if (!farm.postcode.trim()) next.postcode = 'Postcode is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // ---------- Step 2: Produce search ----------
  // Fetch produce suggestions as the user types. Debounce with a small timeout.
  useEffect(() => {
    if (!produceQuery || produceQuery.length < 2) {
      setProduceResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setProduceSearching(true);
      try {
        // Same source the registration wizard uses.
        const { data, error } = await supabase
          .from('produce_view')
          .select('*')
          .or(`name_produce.ilike.%${produceQuery}%,name_produce_malay.ilike.%${produceQuery}%,combined_produce_name.ilike.%${produceQuery}%`)
          .limit(8);
        if (!error && data) setProduceResults(data);
      } catch (e) {
        // Non-critical — search just shows empty.
      } finally {
        setProduceSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [produceQuery]);

  const pickProduce = (p) => {
    setCycle((c) => ({
      ...c,
      produce: p,
      // Auto-generate a cycle name if the user hasn't typed one yet
      name: c.name || `${p.name_produce || p.combined_produce_name || 'Cycle'} — ${today}`,
    }));
    setProduceQuery(p.name_produce || p.combined_produce_name || '');
    setProduceResults([]);
    if (errors.produce) setErrors((e) => ({ ...e, produce: '' }));
  };

  const clearProduce = () => {
    setCycle((c) => ({ ...c, produce: null }));
    setProduceQuery('');
    setProduceResults([]);
  };

  const setCycleField = (name, value) => {
    setCycle((c) => ({ ...c, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: '' }));
  };

  const validateStep2 = () => {
    const next = {};
    if (!cycle.produce) next.produce = 'Please pick a produce';
    if (!cycle.name.trim()) next.name = 'Cycle name is required';
    if (!cycle.dateStart) next.dateStart = 'Start date is required';
    if (!cycle.dateEnd) next.dateEnd = 'Expected harvest date is required';
    if (cycle.dateStart && cycle.dateEnd && new Date(cycle.dateEnd) <= new Date(cycle.dateStart))
      next.dateEnd = 'Harvest date must be after start date';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // ---------- Final submit ----------
  const handleCreate = useCallback(async () => {
    if (!validateStep2()) return;
    if (!user?.id) {
      setSubmitError('Not authenticated');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      // 1. Look up the user's integer id from the user table (using auth.user.id)
      const { data: userRow, error: userErr } = await supabase
        .from('user')
        .select('id')
        .eq('id_auth', user.id)
        .single();
      if (userErr || !userRow) {
        throw new Error('Could not resolve your user record. Please log out and back in.');
      }
      const userId = userRow.id;

      // 2. Create address record
      const addressFull = [
        farm.addressLine1,
        farm.addressLine2,
        farm.city,
        `${farm.state} ${farm.postcode}`.trim(),
      ].filter(Boolean).join(', ');
      const { data: address, error: addrErr } = await supabase
        .from('address')
        .insert({
          addressLine1: farm.addressLine1,
          addressLine2: farm.addressLine2 || null,
          city: farm.city,
          state: farm.state,
          postcode: farm.postcode,
          latitude: farm.latitude ? parseFloat(farm.latitude) : null,
          longitude: farm.longitude ? parseFloat(farm.longitude) : null,
          addressFull,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (addrErr) throw addrErr;

      // 3. Create organisation (one per new farm, same pattern as SetupFarm).
      //    Not fatal if it fails — the farm can still be created without it.
      let orgId = null;
      const { data: org } = await supabase
        .from('organisation')
        .insert({
          name_org: `${farm.farmName} Organisation`,
          company: false,
          created_by: userId,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();
      orgId = org?.id || null;

      // 4. Create the farm
      const { data: newFarm, error: farmErr } = await supabase
        .from('farm')
        .insert({
          farmName: farm.farmName,
          farmsize: parseFloat(farm.farmSize),
          address: address.id,
          organisation: orgId,
          currency: 1, // MYR default
          created_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (farmErr) throw farmErr;

      // 5. Link user to farm as Owner
      const { data: fur } = await supabase
        .from('farm_user_role')
        .insert({
          farm: newFarm.id,
          user: userId,
          role: 2, // Owner
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      // 6. Update user's default farm / default_fur so the new farm becomes
      //    the focused one after creation.
      if (fur?.id) {
        await supabase.from('user').update({
          farm_default: newFarm.id,
          default_fur: fur.id,
        }).eq('id', userId);
      }

      // 7. Ensure a user_org + user_org_farm link exists to SMARTFARM org so
      //    admin filtering still finds this farm. Skip errors if already linked.
      let userOrgId = null;
      const { data: existingUserOrg } = await supabase
        .from('user_org')
        .select('id')
        .eq('user', userId)
        .eq('organisation', SMARTFARM_ORGANISATION_ID)
        .maybeSingle();
      if (existingUserOrg?.id) {
        userOrgId = existingUserOrg.id;
      } else {
        const { data: userOrg } = await supabase
          .from('user_org')
          .insert({
            user: userId,
            organisation: SMARTFARM_ORGANISATION_ID,
            created_at: new Date().toISOString(),
          })
          .select()
          .single();
        userOrgId = userOrg?.id || null;
      }
      if (userOrgId) {
        await supabase.from('user_org_farm').insert({
          user_org: userOrgId,
          farm: newFarm.id,
          created_at: new Date().toISOString(),
        });
      }

      // 8. Create the first cycle / season
      const { data: season, error: seasonErr } = await supabase
        .from('season')
        .insert({
          farm: newFarm.id,
          name_season: cycle.name,
          produce_name: cycle.produce.id,
          date_start: cycle.dateStart,
          date_end: cycle.dateEnd,
          expected_harvest_amount: cycle.expectedHarvest ? parseFloat(cycle.expectedHarvest) : null,
          created_by: userId,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (seasonErr) throw seasonErr;

      // 9. Make this season the user's default season + the fur's default
      if (season?.id) {
        await supabase.from('user').update({ season_default: season.id }).eq('id', userId);
        if (fur?.id) {
          await supabase.from('farm_user_role').update({ default_season: season.id }).eq('id', fur.id);
        }
      }

      onFarmCreated && onFarmCreated({
        farmId: newFarm.id,
        farmName: newFarm.farmName,
        cycleId: season?.id,
        cycleName: season?.name_season,
      });
    } catch (e) {
      console.error('CreateFarmWithCycle error:', e);
      setSubmitError(e?.message || 'Could not create the farm. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [user, farm, cycle, onFarmCreated]);

  // ---------- Render helpers ----------
  const Stepper = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2].map((n) => {
        const active = step === n;
        const done = step > n;
        return (
          <React.Fragment key={n}>
            <div className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                done ? 'bg-green-500 text-white' :
                active ? 'bg-blue-600 text-white' :
                'bg-gray-200 text-gray-500'
              }`}>
                {done ? <CheckCircleIcon className="h-5 w-5" /> : n}
              </div>
              <span className={`text-sm font-medium ${active ? 'text-blue-700' : done ? 'text-green-600' : 'text-gray-500'}`}>
                {n === 1 ? 'Farm' : 'First Cycle'}
              </span>
            </div>
            {n < 2 && <div className={`h-0.5 w-10 ${step > n ? 'bg-green-500' : 'bg-gray-200'}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <BuildingStorefrontIcon className="h-6 w-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">Create New Farm</h2>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Enter your farm details and register your first growing cycle — you'll be set as the farm owner automatically.
          </p>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <XMarkIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      <Stepper />

      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
          {submitError}
        </div>
      )}

      {/* STEP 1 — FARM */}
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Farm Name *</label>
            <input
              type="text"
              value={farm.farmName}
              onChange={(e) => setFarmField('farmName', e.target.value)}
              className={`input w-full ${errors.farmName ? 'border-red-300' : ''}`}
              placeholder="e.g. Green Valley Farm"
            />
            {errors.farmName && <p className="mt-1 text-sm text-red-600">{errors.farmName}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Farm Size (hectares) *</label>
            <input
              type="number"
              step="0.01"
              value={farm.farmSize}
              onChange={(e) => setFarmField('farmSize', e.target.value)}
              className={`input w-full ${errors.farmSize ? 'border-red-300' : ''}`}
              placeholder="e.g. 5.2"
            />
            {errors.farmSize && <p className="mt-1 text-sm text-red-600">{errors.farmSize}</p>}
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <MapPinIcon className="h-5 w-5 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-800">Location</h3>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1 *</label>
              <input
                type="text"
                value={farm.addressLine1}
                onChange={(e) => setFarmField('addressLine1', e.target.value)}
                className={`input w-full ${errors.addressLine1 ? 'border-red-300' : ''}`}
                placeholder="Street address"
              />
              {errors.addressLine1 && <p className="mt-1 text-sm text-red-600">{errors.addressLine1}</p>}
            </div>

            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
              <input
                type="text"
                value={farm.addressLine2}
                onChange={(e) => setFarmField('addressLine2', e.target.value)}
                className="input w-full"
                placeholder="Apt, suite, unit (optional)"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <input
                  type="text"
                  value={farm.city}
                  onChange={(e) => setFarmField('city', e.target.value)}
                  className={`input w-full ${errors.city ? 'border-red-300' : ''}`}
                  placeholder="City"
                />
                {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postcode *</label>
                <input
                  type="text"
                  value={farm.postcode}
                  onChange={(e) => setFarmField('postcode', e.target.value)}
                  className={`input w-full ${errors.postcode ? 'border-red-300' : ''}`}
                  placeholder="e.g. 43000"
                />
                {errors.postcode && <p className="mt-1 text-sm text-red-600">{errors.postcode}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                <select
                  value={farm.state}
                  onChange={(e) => setFarmField('state', e.target.value)}
                  className={`input w-full ${errors.state ? 'border-red-300' : ''}`}
                >
                  <option value="">Select state</option>
                  {MALAYSIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.state && <p className="mt-1 text-sm text-red-600">{errors.state}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <input type="text" value="Malaysia" readOnly className="input w-full bg-gray-100 cursor-not-allowed" />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-3">
            {onClose && (
              <button onClick={onClose} className="btn-secondary text-sm">
                Cancel
              </button>
            )}
            <button
              onClick={() => { if (validateStep1()) setStep(2); }}
              className="btn-primary flex items-center gap-2 ml-auto"
            >
              Next: First Cycle
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 — FIRST CYCLE */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
            Every farm needs a growing cycle. Tell us what you're planting first — you can add more cycles later.
          </div>

          {/* Produce picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">What are you growing? *</label>
            {cycle.produce ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  {cycle.produce.icon && (
                    <img src={cycle.produce.icon} alt="" className="h-10 w-10 rounded-full object-cover bg-white border" />
                  )}
                  <div>
                    <div className="font-semibold text-green-900">
                      {cycle.produce.name_produce || cycle.produce.combined_produce_name}
                    </div>
                    {cycle.produce.name_produce_malay && (
                      <div className="text-xs text-green-700">{cycle.produce.name_produce_malay}</div>
                    )}
                  </div>
                </div>
                <button onClick={clearProduce} className="text-gray-500 hover:text-gray-800 text-sm">Change</button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={produceQuery}
                    onChange={(e) => setProduceQuery(e.target.value)}
                    className={`input w-full pl-10 ${errors.produce ? 'border-red-300' : ''}`}
                    placeholder="Search produce (e.g. durian, padi, lettuce)"
                  />
                </div>
                {produceSearching && (
                  <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <ArrowPathIcon className="h-3 w-3 animate-spin" /> Searching...
                  </div>
                )}
                {produceResults.length > 0 && (
                  <div className="mt-2 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                    {produceResults.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => pickProduce(p)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                      >
                        {p.icon && (
                          <img src={p.icon} alt="" className="h-8 w-8 rounded-full object-cover bg-gray-50 border" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {p.name_produce || p.combined_produce_name}
                          </div>
                          {p.name_produce_malay && (
                            <div className="text-xs text-gray-500">{p.name_produce_malay}</div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {errors.produce && <p className="mt-1 text-sm text-red-600">{errors.produce}</p>}
              </>
            )}
          </div>

          {/* Cycle name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Name *</label>
            <input
              type="text"
              value={cycle.name}
              onChange={(e) => setCycleField('name', e.target.value)}
              className={`input w-full ${errors.name ? 'border-red-300' : ''}`}
              placeholder="e.g. Durian D24 — Batch June"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <CalendarIcon className="h-4 w-4" /> Planting Start Date *
              </label>
              <input
                type="date"
                value={cycle.dateStart}
                onChange={(e) => setCycleField('dateStart', e.target.value)}
                className={`input w-full ${errors.dateStart ? 'border-red-300' : ''}`}
              />
              {errors.dateStart && <p className="mt-1 text-sm text-red-600">{errors.dateStart}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <CalendarIcon className="h-4 w-4" /> Expected Harvest Date *
              </label>
              <input
                type="date"
                value={cycle.dateEnd}
                onChange={(e) => setCycleField('dateEnd', e.target.value)}
                className={`input w-full ${errors.dateEnd ? 'border-red-300' : ''}`}
              />
              {errors.dateEnd && <p className="mt-1 text-sm text-red-600">{errors.dateEnd}</p>}
            </div>
          </div>

          {/* Expected yield (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Yield (kg)</label>
            <input
              type="number"
              step="0.1"
              value={cycle.expectedHarvest}
              onChange={(e) => setCycleField('expectedHarvest', e.target.value)}
              className="input w-full"
              placeholder="e.g. 1000"
            />
            <p className="text-xs text-gray-500 mt-1">Optional — you can update this later.</p>
          </div>

          <div className="flex items-center justify-between pt-3">
            <button
              onClick={() => setStep(1)}
              className="btn-secondary flex items-center gap-2 text-sm"
              disabled={submitting}
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Back
            </button>
            <button
              onClick={handleCreate}
              disabled={submitting}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4" />
                  Create Farm
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateFarmWithCycle;
